import moment from "moment";
import Enumerable from "linq";
import { ValueReferenceParameter, ILinkedDocumentRepository, LinkedDocumentSavedInfo } from "../../../domain/collaboration";
import { AuthenticatedService } from "../..";
import { IValueReferenceParameterBean, ILinkedDocumentSavedInfoBean } from "./beans";
import { ICurrentEditorBean } from "../../document/ver2/beans";
import { CurrentEditors } from "../../../domain/document";

export class LinkedDocumentRepository extends AuthenticatedService implements ILinkedDocumentRepository {
  constructor(getToken: () => string) {
    super(getToken);
  }
  
  public async getDocumentId(systemKey: string, linkKey: string, patientCode: string): Promise<number> {
    if (!systemKey) { throw new Error("systemKey is empty!!!"); }
    if (!linkKey) { throw new Error("linkKey is empty!!!"); }
    if (!patientCode) { throw new Error("patientCode is empty!!!"); }
    
    try {
      const url = "/api/collaboration/link/documentid";
      const query = {
        params: {
          systemKey,
          linkKey,
          patientCode,
        }
      };
      const result = await this.get(url, query);
      return result.data as number;
    } catch (e) {
      if (e.response.status === 404) {
        return null as number;
      }
      throw e;
    }
  }

  public async getDocumentSavedInfo(systemKey: string, linkKey: string, patientCode: string): Promise<LinkedDocumentSavedInfo> {
    if (!systemKey) { throw new Error("systemKey is empty!!!"); }
    if (!linkKey) { throw new Error("linkKey is empty!!!"); }
    if (!patientCode) { throw new Error("patientCode is empty!!!"); }
    
    try {
      const url = "/api/collaboration/link/document-saved-info";
      const query = {
        params: {
          systemKey,
          linkKey,
          patientCode,
        }
      };
      const result = await this.get(url, query);
      return this.toLinkedDocumentSavedInfo(result.data);
    } catch (e) {
      if (e.response.status === 404) {
        return null;
      }
      throw e;
    }
  }

  private toLinkedDocumentSavedInfo(bean: ILinkedDocumentSavedInfoBean): LinkedDocumentSavedInfo {
    if (!bean) { return null; }
    const result = new LinkedDocumentSavedInfo();
    result.documentId = bean.documentId;
    result.createUserName = bean.createUserName;
    return result;
  }

  public async getValueReferenceParameter(documentId: number): Promise<ValueReferenceParameter> {
    const url = "/api/collaboration/value-reference-parameter/" + documentId;
    const result = await this.get(url);
    console.debug(result.data);
    return this.toValueReferenceParameter(result.data);
  }

  private toValueReferenceParameter(bean: IValueReferenceParameterBean): ValueReferenceParameter {
    if (!bean) { return null; }
    const result = new ValueReferenceParameter();
    result.systemKey = bean.systemKey;
    result.linkKey = bean.linkKey;
    result.institutionNo = bean.institutionNo;
    result.regionalInstitutionNo = bean.regionalInstitutionNo;
    result.hisInfoGetterParameter = bean.hisInfoGetterParameter;
    return result;
  }

  public async getDocumentCurrentEditors(systemKey: string, linkKey: string, patientCode: string, exclusionToken: string): Promise<CurrentEditors[]> {
    if (!systemKey) { throw new Error("systemKey is empty!!!"); }
    if (!linkKey) { throw new Error("linkKey is empty!!!"); }
    if (!patientCode) { throw new Error("patientCode is empty!!!"); }

    try {
      const url = "/api/collaboration/link/document-current-editors";
      const query = {
        params: {
          patientCode,
          systemKey,
          linkKey,
          exclusionToken
        }
      };
      const result = await this.get(url, query);
      return this.toCurrentEditors(result.data);
    } catch (e) {
      if (e.response.status === 404) {
        return null;
      }
      throw e;
    }
  }

  private toCurrentEditors(beans: ICurrentEditorBean[]): CurrentEditors[] {
    if (!beans) { return []; }
    return Enumerable.from(beans).select(b => this.toCurrentEditor(b)).toArray();
  }

  private toCurrentEditor(bean: ICurrentEditorBean): CurrentEditors {
    return new CurrentEditors(
      bean.userName,
      bean.terminalName,
      this.toDate(bean.editStartDateTime),
      bean.token,
    );
  }

  private toDate(dateTimeAsString: string): Date {
    if (!dateTimeAsString) { return new Date(); }
    const parsedDateTime = moment(dateTimeAsString, "YYYYMMDDHHmmss").toDate();
    return !parsedDateTime
      ? new Date()
      : parsedDateTime;
  }
}
